import React, { useCallback, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Avatar,
  Tooltip,
  IconButton,
  Chip,
  SvgIcon,
  useTheme,
  useMediaQuery,
  Button
} from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import type {
  OpportunityWithSnapshot
} from '../../types/opportunity';
import {
  getExchangeName,
  getQuoteCurrency,
  getQuoteCurrencySymbol,
  makeOpportunityKey
} from '../../types/opportunity';
import { ElapsedTime } from './ElapsedTime';
import { OrderbookTooltip } from './OrderbookTooltip';
import { NetworkStatusTooltip } from './NetworkStatusTooltip';
import { ProfitCalculationTooltip } from './ProfitCalculationTooltip';
import { FuturesDataRow } from './FuturesDataRow';
import { getExchangeLogo, ExchangeUrls } from '../../utils/exchangeUrls';
import { formatPrice, formatVolume, formatTotalValue, getProfitColor } from '../../utils/formatters';
import { useAppSelector, useAppDispatch } from '../../app/hooks';
import { starOpportunity, unstarOpportunity } from '../../features/snapshots/snapshotsSlice';
import {
  formatChangePercentage,
  formatVolumeChange,
  formatProfitChange,
  formatTimeSinceSnapshot,
  getChangeColor
} from '../../utils/snapshotComparison';
import {
  getArbitrageTransferStatus,
  getCryptoNetworkDetails,
  hasNetworkStatusData,
  type TransferStatus
} from '../../utils/networkCompatibility';
import { NETWORK_STATUS_COLORS } from '../../constants/colors';
import type { SvgIconProps } from '@mui/material/SvgIcon';
import { useMarkPrice } from '../../hooks/useMarkPrice';
import { useSettings } from '../../hooks/useSettings';
import { isExecutableOpportunity } from '../../utils/strategyHelpers';
import { StrategyExecutionDialog } from './StrategyExecutionDialog';
import { useTraderName } from '../../contexts/TraderNameContext';

interface OpportunityRowSectionProps {
  opportunity: OpportunityWithSnapshot;
  index: number;
  networkStatusStale?: boolean;
  networkStatusAvailable?: boolean;
}

// Helper function to get border color based on transfer status
const getStatusBorderColor = (status: TransferStatus): string => {
  switch (status) {
    case 'enabled':
      return NETWORK_STATUS_COLORS.ENABLED;
    case 'disabled':
      return NETWORK_STATUS_COLORS.DISABLED;
    case 'unknown':
      return NETWORK_STATUS_COLORS.UNKNOWN;
    default:
      return 'transparent';
  }
};

const BinocularsIcon: React.FC<SvgIconProps> = (props) => (
  <SvgIcon viewBox="0 0 24 24" {...props}>
    <path d="M7 3c-.55 0-1 .45-1 1v4.5L4.92 13a3 3 0 0 0-.17.98V20h3v-5h1v5h3v-9.5l-2-2.5V4c0-.55-.45-1-1-1H7Zm10 0c-.55 0-1 .45-1 1v4l-2 2.5V20h3v-5h1v5h3v-6c0-.33-.06-.66-.17-.97L18 8.5V4c0-.55-.45-1-1-1h-1Z" />
    <circle cx="7.5" cy="16.5" r="2.5" />
    <circle cx="16.5" cy="16.5" r="2.5" />
  </SvgIcon>
);

export const OpportunityRowSection: React.FC<OpportunityRowSectionProps> = React.memo(({
  opportunity,
  index,
  networkStatusStale,
  networkStatusAvailable
}) => {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [buyLogoError, setBuyLogoError] = useState(false);
  const [sellLogoError, setSellLogoError] = useState(false);
  const [executeDialogOpen, setExecuteDialogOpen] = useState(false);

  // Get settings for budget check
  const { settings } = useSettings();

  // Get trader name for strategy execution
  const traderName = useTraderName();

  // Check if this opportunity is starred
  const opportunityKey = makeOpportunityKey(opportunity);
  const isStarred = opportunity.isStarred || false;

  // Get network status from Redux store
  const networkStatus = useAppSelector(state => state.networkStatus?.data || []);

  // Use pre-calculated display values or calculate from raw data
  const buyExchange = getExchangeName(opportunity.buy_exchange);
  const sellExchange = getExchangeName(opportunity.sell_exchange);
  const buyExchangeSlug = buyExchange.toLowerCase();
  const sellExchangeSlug = sellExchange.toLowerCase();
  const buyQuoteCurrency = getQuoteCurrency(opportunity.buy_quote);
  const sellQuoteCurrency = getQuoteCurrency(opportunity.sell_quote);
  const buySymbol = `${opportunity.base}${buyQuoteCurrency}`;
  const sellSymbol = `${opportunity.base}${sellQuoteCurrency}`;
  const browseUrl = useMemo(
    () => `/orderbooks/${buyExchangeSlug}/${buySymbol}/${sellExchangeSlug}/${sellSymbol}`,
    [buyExchangeSlug, buySymbol, sellExchangeSlug, sellSymbol],
  );
  
  // Get transfer status for this opportunity
  const transferStatus = networkStatus && hasNetworkStatusData(networkStatus)
    ? getArbitrageTransferStatus(opportunity, networkStatus)
    : { withdrawStatus: 'unknown' as const, depositStatus: 'unknown' as const };
  
  // Get network details for the base currency
  const buyCrypto = networkStatus && hasNetworkStatusData(networkStatus)
    ? getCryptoNetworkDetails(opportunity.base, opportunity.buy_exchange, networkStatus)
    : null;
  const sellCrypto = networkStatus && hasNetworkStatusData(networkStatus)
    ? getCryptoNetworkDetails(opportunity.base, opportunity.sell_exchange, networkStatus)
    : null;
  
  // Buy and sell prices use LAST level (deepest level)
  const buyPrice = opportunity.buy_price || 
    (opportunity.buy_levels && opportunity.buy_levels.length > 0 
      ? opportunity.buy_levels[opportunity.buy_levels.length - 1]?.price 
      : 0) || 0;
  const sellPrice = opportunity.sell_price || 
    (opportunity.sell_levels && opportunity.sell_levels.length > 0
      ? opportunity.sell_levels[opportunity.sell_levels.length - 1]?.price
      : 0) || 0;
  
  // Calculate profit percentage
  const profitRatio = opportunity.profit_ratio || 1.0;
  const profitPercentage = (profitRatio - 1.0) * 100;
  
  // Total buy value
  const buyTotalValue = opportunity.total_buy_value || 
    (opportunity.buy_levels && opportunity.buy_levels.length > 0
      ? opportunity.buy_levels.reduce((sum, level) => sum + (level.price * level.volume), 0)
      : 0);

  // Construct trading pair string with currency symbols on mobile, full names on desktop
  const buyQuoteDisplay = isMobile
    ? getQuoteCurrencySymbol(opportunity.buy_quote)
    : buyQuoteCurrency;
  const sellQuoteDisplay = isMobile
    ? getQuoteCurrencySymbol(opportunity.sell_quote)
    : sellQuoteCurrency;

  const tradingPair = `${opportunity.base}/${buyQuoteDisplay}`;
  const sellPair = `${opportunity.base}/${sellQuoteDisplay}`;

  // Handle exchange click
  const handleBuyClick = useCallback(() => {
    const url = ExchangeUrls.getSpotTradeUrl(
      opportunity.buy_exchange, 
      opportunity.base, 
      buyQuoteCurrency
    );
    if (url) window.open(url, '_blank');
  }, [buyQuoteCurrency, opportunity.base, opportunity.buy_exchange]);

  const handleSellClick = useCallback(() => {
    const url = ExchangeUrls.getSpotTradeUrl(
      opportunity.sell_exchange,
      opportunity.base,
      sellQuoteCurrency
    );
    if (url) window.open(url, '_blank');
  }, [opportunity.base, opportunity.sell_exchange, sellQuoteCurrency]);

  // Handle star/unstar click
  const handleStarClick = useCallback(() => {
    if (isStarred) {
      dispatch(unstarOpportunity(opportunityKey));
    } else {
      dispatch(starOpportunity(opportunity));
    }
  }, [dispatch, isStarred, opportunity, opportunityKey]);
  
  const handleBrowseClick = useCallback(() => {
    window.open(browseUrl, '_blank', 'noopener,noreferrer');
  }, [browseUrl]);

  // Handle Execute button click
  const handleExecuteClick = useCallback(() => {
    setExecuteDialogOpen(true);
  }, []);

  // Check if opportunity is executable (exchange restrictions)
  const canExecute = useMemo(() => isExecutableOpportunity(opportunity), [opportunity]);

  // Check if button should be enabled (executable + budget configured)
  const executeButtonEnabled = canExecute && settings.strategyBudget >= 5;
  
  // Row background color - starred items have special background
  const bgColor = opportunity.isStarred 
    ? 'rgba(138, 43, 226, 0.1)' // Light purple/violet for starred
    : index % 2 === 0 
      ? 'rgba(0, 0, 0, 0.3)' 
      : 'transparent';
  
  // Add glow effect for starred items
  const rowBoxShadow = opportunity.isStarred
    ? '0 0 20px rgba(138, 43, 226, 0.3), inset 0 0 20px rgba(138, 43, 226, 0.1)'
    : 'none';
  
  // Check if opportunity is deleted
  const isDeleted = opportunity.changes?.isDeleted || false;

  // Fetch futures data for this symbol (Story 8.1 hooks)
  // ALWAYS use USDT for futures lookup - Binance Futures only has USDT pairs
  // For hedging, we compare spot BTC/KRW with futures BTC/USDT, etc.
  const futuresSymbol = `${opportunity.base}/USDT`;
  const markPriceData = useMarkPrice(futuresSymbol);

  // Determine if futures row should be shown
  // Show if mark price data exists (includes bestBid/bestAsk from book ticker stream)
  // Note: Futures orderbooks are no longer subscribed (per Epic 10)
  const shouldShowFuturesRow = useMemo(() => {
    return markPriceData !== undefined;
  }, [markPriceData]);

  // Cell style for consistent spacing - COMPACT VERSION FOR SECTIONS
  const cellStyle = {
    padding: '2px 4px',
    display: 'flex',
    alignItems: opportunity.isStarred ? 'flex-start' : 'center',
    minHeight: '20px',
    paddingTop: opportunity.isStarred ? '4px' : '2px'
  };

  // Tooltip props to ensure it appears above scrollbar and stays within viewport
  const tooltipProps = {
    placement: 'top' as const,
    arrow: true,
    enterDelay: 50,
    leaveDelay: 200,
    disablePortal: false,
    PopperProps: {
      sx: {
        zIndex: 9999,
        pointerEvents: 'none',
      },
      container: document.body,
      modifiers: [
        {
          name: 'offset',
          options: {
            offset: [0, -8],
          },
        },
        {
          name: 'preventOverflow',
          enabled: true,
          options: {
            boundary: 'viewport',
            padding: 16,
            altAxis: true,
            mainAxis: true,
          },
        },
        {
          name: 'flip',
          enabled: true,
          options: {
            fallbackPlacements: ['bottom', 'left', 'right', 'top'],
            padding: 16,
          },
        },
        {
          name: 'hide',
          enabled: false,
        },
      ],
    }
  };

  return (
    <Box sx={{
      backgroundColor: bgColor,
      display: 'flex',
      position: 'relative',
      paddingTop: '6px',
      paddingBottom: '6px',
      minHeight: opportunity.isStarred ? '70px' : '60px',
      width: '100%',
      maxWidth: '100%',
      boxSizing: 'border-box',
      boxShadow: rowBoxShadow,
      border: opportunity.isStarred ? '1px solid rgba(138, 43, 226, 0.3)' : 'none',
      opacity: isDeleted ? 0.7 : 1,
      transition: 'all 0.3s ease',
      '&:hover': {
        backgroundColor: opportunity.isStarred
          ? 'rgba(138, 43, 226, 0.15)'
          : 'rgba(255, 255, 255, 0.05)'
      }
    }}>
      {/* DELETED chip if opportunity was deleted */}
      {isDeleted && (
        <Chip
          label="DELETED"
          size="small"
          sx={{
            position: 'absolute',
            top: 2,
            right: 2,
            backgroundColor: 'rgba(255, 0, 0, 0.2)',
            color: '#ff3333',
            fontSize: '0.6rem',
            height: '16px',
            zIndex: 1
          }}
        />
      )}

      {/* Star button - 5% */}
      <Box
        sx={{
          width: '5%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 0.25,
        }}
      >
        <IconButton
          onClick={handleStarClick}
          size="small"
          sx={{
            p: 0,
            color: isStarred ? '#ffd700' : 'rgba(255, 255, 255, 0.3)',
          }}
        >
          {isStarred ? <StarIcon sx={{ fontSize: 16 }} /> : <StarBorderIcon sx={{ fontSize: 16 }} />}
        </IconButton>
        <Tooltip title="Browse orderbooks" arrow placement="top">
          <IconButton
            onClick={handleBrowseClick}
            size="small"
            sx={{
              p: 0.25,
              color: 'rgba(255, 255, 255, 0.7)',
            }}
            aria-label="open orderbooks"
          >
            <BinocularsIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>

        {/* Execute Strategy Button - Only visible for Binance/Bithumb buy → Binance/Bithumb/Upbit sell */}
        {canExecute && (
          <Tooltip
            title={
              executeButtonEnabled
                ? "Execute strategy for this opportunity"
                : `Budget not configured (Current: $${settings.strategyBudget})`
            }
            arrow
            placement="top"
          >
            <span>
              <IconButton
                onClick={handleExecuteClick}
                color="primary"
                size="small"
                disabled={!executeButtonEnabled}
                sx={{
                  mt: 0.5,
                  p: 0.25,
                  backgroundColor: !executeButtonEnabled ? 'rgba(255, 255, 255, 0.12)' : 'rgba(25, 118, 210, 0.5)',
                  '&:disabled': {
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    opacity: 0.3,
                  },
                }}
              >
                <RocketLaunchIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Box>
      
      {/* Left side - 85% (compact for sections) */}
      <Box sx={{ width: '85%' }}>
        {/* Buy side row */}
        <Box display="flex">
          <Box sx={{ ...cellStyle, width: '35.29%', cursor: 'pointer' }} onClick={handleBuyClick}>
            <Box display="flex" alignItems="center" gap={0.5}>
              {!buyLogoError ? (
                <Tooltip
                  title={
                    <NetworkStatusTooltip
                      symbol={opportunity.base}
                      buyExchange={opportunity.buy_exchange}
                      sellExchange={opportunity.sell_exchange}
                      buyCrypto={buyCrypto}
                      sellCrypto={sellCrypto}
                    />
                  }
                  {...tooltipProps}
                >
                  <Avatar
                    src={getExchangeLogo(opportunity.buy_exchange)}
                    sx={{
                      width: 16,
                      height: 16,
                      cursor: 'pointer',
                      border: `2px solid ${getStatusBorderColor(transferStatus.withdrawStatus)}`,
                      boxShadow: transferStatus.withdrawStatus === 'enabled'
                        ? `0 0 8px ${NETWORK_STATUS_COLORS.ENABLED}`
                        : 'none',
                      '&:hover': {
                        transform: 'scale(1.1)',
                        transition: 'transform 0.2s'
                      }
                    }}
                    onError={() => setBuyLogoError(true)}
                  >
                    {buyExchange[0]}
                  </Avatar>
                </Tooltip>
              ) : (
                <Tooltip
                  title={
                    <NetworkStatusTooltip
                      symbol={opportunity.base}
                      buyExchange={opportunity.buy_exchange}
                      sellExchange={opportunity.sell_exchange}
                      buyCrypto={buyCrypto}
                      sellCrypto={sellCrypto}
                    />
                  }
                  {...tooltipProps}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      minWidth: 16,
                      textAlign: 'center',
                      cursor: 'pointer',
                      border: `2px solid ${getStatusBorderColor(transferStatus.withdrawStatus)}`,
                      borderRadius: '50%',
                      fontSize: { xs: '0.7rem', md: '0.85rem' },
                      padding: '2px',
                      display: 'inline-block',
                      lineHeight: 1,
                      boxShadow: transferStatus.withdrawStatus === 'enabled'
                        ? `0 0 8px ${NETWORK_STATUS_COLORS.ENABLED}`
                        : 'none'
                    }}
                  >
                    {buyExchange.substring(0, 3).toUpperCase()}
                  </Typography>
                </Tooltip>
              )}


              {/* Trading Pair */}
              <Typography variant="body2" color="text.secondary" sx={{
                fontSize: { xs: '0.8rem', md: '0.95rem' }
              }}>
                {tradingPair}
              </Typography>
            </Box>
          </Box>

          {/* Buy Price - 32.35% */}
          <Tooltip
            title={
              <OrderbookTooltip
                levels={opportunity.buy_levels}
                side="buy"
                quote={getQuoteCurrency(opportunity.buy_quote)}
                exchange={opportunity.buy_exchange}
                exchangeName={buyExchange}
                tradingPair={tradingPair}
              />
            }
            {...tooltipProps}
          >
            <Box sx={{ ...cellStyle, width: '32.35%', justifyContent: 'flex-end', pr: 1, cursor: 'help' }}>
              <Box display="flex" flexDirection="column" alignItems="flex-end">
                <Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', md: '0.95rem' } }}>
                  {formatPrice(buyPrice, opportunity.buy_quote)}
                </Typography>
                {/* Show price change for starred items */}
                {opportunity.isStarred && opportunity.changes && opportunity.changes.buyPriceChange !== 0 && (
                  <Box display="flex" alignItems="center" gap={0.5}>
                    {opportunity.changes.buyPriceChange > 0 ? (
                      <TrendingUpIcon sx={{ fontSize: 10, color: getChangeColor(opportunity.changes.buyPriceChange, true) }} />
                    ) : (
                      <TrendingDownIcon sx={{ fontSize: 10, color: getChangeColor(opportunity.changes.buyPriceChange, true) }} />
                    )}
                    <Typography variant="caption" sx={{ fontSize: '0.65rem', color: getChangeColor(opportunity.changes.buyPriceChange, true) }}>
                      {formatChangePercentage(opportunity.changes.buyPriceChange)}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </Tooltip>

          {/* Buy Volume - 32.35% */}
          <Box sx={{ ...cellStyle, width: '32.35%' }}>
            <Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', md: '0.95rem' } }}>
              {formatVolume(opportunity.buy_levels.reduce((sum, level) => sum + level.volume, 0))}
            </Typography>
          </Box>
        </Box>

        {/* Sell side row */}
        <Box display="flex">
          <Box sx={{ ...cellStyle, width: '35.29%', cursor: 'pointer' }} onClick={handleSellClick}>
            <Box display="flex" alignItems="center" gap={0.5}>
              {!sellLogoError ? (
                <Tooltip
                  title={
                    <NetworkStatusTooltip
                      symbol={opportunity.base}
                      buyExchange={opportunity.buy_exchange}
                      sellExchange={opportunity.sell_exchange}
                      buyCrypto={buyCrypto}
                      sellCrypto={sellCrypto}
                    />
                  }
                  {...tooltipProps}
                >
                  <Avatar
                    src={getExchangeLogo(opportunity.sell_exchange)}
                    sx={{
                      width: 16,
                      height: 16,
                      cursor: 'pointer',
                      border: `2px solid ${getStatusBorderColor(transferStatus.depositStatus)}`,
                      boxShadow: transferStatus.depositStatus === 'enabled'
                        ? `0 0 8px ${NETWORK_STATUS_COLORS.ENABLED}`
                        : 'none',
                      '&:hover': {
                        transform: 'scale(1.1)',
                        transition: 'transform 0.2s'
                      }
                    }}
                    onError={() => setSellLogoError(true)}
                  >
                    {sellExchange[0]}
                  </Avatar>
                </Tooltip>
              ) : (
                <Tooltip
                  title={
                    <NetworkStatusTooltip
                      symbol={opportunity.base}
                      buyExchange={opportunity.buy_exchange}
                      sellExchange={opportunity.sell_exchange}
                      buyCrypto={buyCrypto}
                      sellCrypto={sellCrypto}
                    />
                  }
                  {...tooltipProps}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      minWidth: 16,
                      textAlign: 'center',
                      cursor: 'pointer',
                      border: `2px solid ${getStatusBorderColor(transferStatus.depositStatus)}`,
                      borderRadius: '50%',
                      fontSize: { xs: '0.7rem', md: '0.85rem' },
                      padding: '2px',
                      display: 'inline-block',
                      lineHeight: 1,
                      boxShadow: transferStatus.depositStatus === 'enabled'
                        ? `0 0 8px ${NETWORK_STATUS_COLORS.ENABLED}`
                        : 'none'
                    }}
                  >
                    {sellExchange.substring(0, 3).toUpperCase()}
                  </Typography>
                </Tooltip>
              )}


              {/* Trading Pair */}
              <Typography variant="body2" color="text.secondary" sx={{
                fontSize: { xs: '0.8rem', md: '0.95rem' }
              }}>
                {sellPair}
              </Typography>
            </Box>
          </Box>

          {/* Sell Price - 32.35% */}
          <Tooltip
            title={
              <OrderbookTooltip
                levels={opportunity.sell_levels}
                side="sell"
                quote={getQuoteCurrency(opportunity.sell_quote)}
                exchange={opportunity.sell_exchange}
                exchangeName={sellExchange}
                tradingPair={sellPair}
              />
            }
            {...tooltipProps}
          >
            <Box sx={{ ...cellStyle, width: '32.35%', justifyContent: 'flex-end', pr: 1, cursor: 'help' }}>
              <Box display="flex" flexDirection="column" alignItems="flex-end">
                <Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', md: '0.95rem' } }}>
                  {formatPrice(sellPrice, opportunity.sell_quote)}
                </Typography>
                {/* Show price change for starred items */}
                {opportunity.isStarred && opportunity.changes && opportunity.changes.sellPriceChange !== 0 && (
                  <Box display="flex" alignItems="center" gap={0.5}>
                    {opportunity.changes.sellPriceChange > 0 ? (
                      <TrendingUpIcon sx={{ fontSize: 10, color: getChangeColor(opportunity.changes.sellPriceChange) }} />
                    ) : (
                      <TrendingDownIcon sx={{ fontSize: 10, color: getChangeColor(opportunity.changes.sellPriceChange) }} />
                    )}
                    <Typography variant="caption" sx={{ fontSize: '0.65rem', color: getChangeColor(opportunity.changes.sellPriceChange) }}>
                      {formatChangePercentage(opportunity.changes.sellPriceChange)}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </Tooltip>

          {/* Total Value (below volume) - 32.35% */}
          <Box sx={{ ...cellStyle, width: '32.35%' }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', md: '0.9rem' } }}>
              {formatTotalValue(buyTotalValue, true, opportunity.buy_quote)}
            </Typography>
          </Box>
        </Box>

        {/* Futures data row (Row 3) - Conditional rendering */}
        {shouldShowFuturesRow && (
          <FuturesDataRow
            symbol={futuresSymbol}
            markPriceData={markPriceData}
            futuresOrderbook={undefined}
            spotBuyLevels={opportunity.buy_levels}
            buyQuote={opportunity.buy_quote}
            cellStyle={cellStyle}
          />
        )}
      </Box>

      {/* Right side - Profit % and Time - 10% */}
      <Box sx={{
        width: '10%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'flex-end',
        pr: 1
      }}>
        <Tooltip
          title={<ProfitCalculationTooltip opportunity={opportunity} />}
          {...tooltipProps}
        >
          <Box sx={{ cursor: 'help' }}>
            <Typography
              variant="body2"
              sx={{
                color: getProfitColor(profitPercentage),
                fontWeight: profitPercentage >= 10 ? 800 : 600,
                fontSize: profitPercentage >= 10 ? { xs: '1rem', md: '1.2rem' } : { xs: '0.9rem', md: '1rem' },
                textShadow: profitPercentage >= 10
                  ? '0 0 20px #ff00ff, 0 0 40px #ff00ff, 0 0 60px #ff00ff'
                  : profitPercentage >= 5
                    ? '0 0 15px #00ffff, 0 0 30px #00ffff'
                    : '0 0 8px rgba(0, 255, 255, 0.3)',
                filter: profitPercentage >= 10 ? 'brightness(1.3)' : 'none'
              }}
            >
              {profitPercentage.toFixed(2)}%
            </Typography>
          {opportunity.isStarred && opportunity.changes && opportunity.changes.profitRatioChange !== 0 && (
            <Typography 
              variant="caption" 
              sx={{ 
                fontSize: '0.6rem', 
                color: getChangeColor(opportunity.changes.profitRatioChange),
                mt: 0.25
              }}
            >
              ({formatProfitChange(opportunity.changes.profitRatioChange)})
            </Typography>
          )}
          </Box>
        </Tooltip>
        <Typography 
          variant="caption" 
          sx={{ 
            fontSize: { xs: '0.65rem', md: '0.8rem' },
            color: 'rgba(255, 255, 255, 0.45)',
            whiteSpace: 'nowrap',
            minWidth: '60px',
            textAlign: 'right'
          }}
        >
          {opportunity.isStarred && opportunity.snapshot ? (
            <Tooltip title={`Starred ${formatTimeSinceSnapshot(opportunity.changes?.timeSinceSnapshot || 0)}`} {...tooltipProps}>
              <span>⭐ <ElapsedTime startTime={opportunity.detected_at} /></span>
            </Tooltip>
          ) : (
            <ElapsedTime startTime={opportunity.detected_at} />
          )}
        </Typography>
      </Box>

      {/* Strategy Execution Dialog */}
      <StrategyExecutionDialog
        open={executeDialogOpen}
        onClose={() => setExecuteDialogOpen(false)}
        opportunity={opportunity}
        traderName={traderName}
      />
    </Box>
  );
});

OpportunityRowSection.displayName = 'OpportunityRowSection';
